import type { ChatToolEvent, MessageThinking } from "@/types/chat.type";

/**
 * One model round of an assistant turn, in the order it streamed: what the model
 * thought, what it said, then the tools it called.
 */
export type TurnRound<T extends ChatToolEvent = ChatToolEvent> = {
  thinking: MessageThinking | null;
  text: string;
  tools: T[];
};

const clamp = (n: number, lo: number, hi: number) => Math.min(Math.max(n, lo), hi);

/** How older rows (no offsets) were always shown: thinking, tools, then text. */
function legacyRounds<T extends ChatToolEvent>(
  content: string,
  thinking: MessageThinking | null,
  events: T[],
): TurnRound<T>[] {
  const rounds: TurnRound<T>[] = [];
  if (thinking || events.length > 0) rounds.push({ thinking, text: "", tools: events });
  if (content.trim()) rounds.push({ thinking: null, text: content.trim(), tools: [] });
  return rounds;
}

/**
 * Splits a finished (or persisted, still-streaming) turn back into its rounds,
 * so it reads exactly as it did live instead of regrouping into one thinking
 * block, every tool, then all the text.
 *
 * The server stamps each tool event with how much text and thinking had streamed
 * when it started. Tools of one round share those offsets; the slice between two
 * rounds' offsets is what the later round thought and said before its tools.
 * Whatever follows the last tool is the closing round.
 */
export function splitTurnRounds<T extends ChatToolEvent>(
  content: string,
  thinking: MessageThinking | null,
  events: T[],
): TurnRound<T>[] {
  if (events.length === 0) return legacyRounds(content, thinking, events);
  if (!events.every((e) => typeof e.textOffset === "number")) {
    return legacyRounds(content, thinking, events);
  }
  // Rows from before thinking offsets existed still place their text and tools;
  // their thinking keeps its old spot, ahead of everything.
  const placesThinking =
    !!thinking && events.every((e) => typeof e.thinkingOffset === "number");
  const thinkingText = thinking?.text ?? "";

  const rounds: TurnRound<T>[] = [];
  let textPos = 0;
  let thinkPos = 0;
  let thinkMsPos = 0;

  for (let i = 0; i < events.length; ) {
    const head = events[i];
    const tools = [head];
    i += 1;
    while (
      i < events.length &&
      events[i].textOffset === head.textOffset &&
      events[i].thinkingOffset === head.thinkingOffset
    ) {
      tools.push(events[i]);
      i += 1;
    }

    const textEnd = clamp(head.textOffset as number, textPos, content.length);
    const text = content.slice(textPos, textEnd).trim();
    textPos = textEnd;

    let roundThinking: MessageThinking | null = null;
    if (placesThinking) {
      const thinkEnd = clamp(head.thinkingOffset as number, thinkPos, thinkingText.length);
      const slice = thinkingText.slice(thinkPos, thinkEnd).trim();
      const msAt = typeof head.thinkingMs === "number" ? head.thinkingMs : thinkMsPos;
      if (slice) roundThinking = { text: slice, durationMs: Math.max(0, msAt - thinkMsPos) };
      thinkPos = thinkEnd;
      thinkMsPos = Math.max(thinkMsPos, msAt);
    } else if (rounds.length === 0) {
      roundThinking = thinking;
    }

    rounds.push({ thinking: roundThinking, text, tools });
  }

  const tailText = content.slice(textPos).trim();
  let tailThinking: MessageThinking | null = null;
  if (placesThinking && thinking) {
    const slice = thinkingText.slice(thinkPos).trim();
    if (slice) {
      tailThinking = {
        text: slice,
        durationMs: Math.max(0, thinking.durationMs - thinkMsPos),
      };
    }
  }
  if (tailText || tailThinking) {
    rounds.push({ thinking: tailThinking, text: tailText, tools: [] });
  }
  return rounds;
}
